import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  exchangeMicrosoftAuthToken,
  getCurrentUser,
  type ApiUser,
} from '@/libs/api/authService.ts';
import { ApiHttpError } from '@/libs/api/errorHandler';
import { registerHttpAuthHandlers, setInMemoryBearerToken } from '@/libs/http';
import {
  extractParentToken,
  getParentAuthErrorMessage,
  getParentLogoutReason,
  isAllowedParentOrigin,
  isParentAuthErrorSignal,
  isParentLogoutSignal,
  isParentTokenExpiredSignal,
  notifyParentChildReady,
  requestTokenFromParent,
} from '@/auth/embeddedTokenBridge';
import { toast } from 'sonner';

const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
const ID_TOKEN_STORAGE_KEY = 'id_token';
const EXCHANGED_ACCOUNT_STORAGE_KEY = 'exchanged_account_id';
const BACKEND_USER_STORAGE_KEY = 'backend_user';
const EMBEDDED_CHILD_APP_ID = process.env.BUN_PUBLIC_EMBEDDED_CHILD_APP_ID || 'di-hr-web';
const EMBEDDED_REFRESH_WAIT_TIMEOUT_MS = 5000;
const authSyncLocks = new Set<string>();
const AUTH_SESSION_EXPIRED_ERROR_CODES = new Set([
  'interaction_required',
  'login_required',
  'monitor_window_timeout',
  'no_account_error',
  'timed_out',
  'token_renewal_error',
  'user_login_error',
]);

const getStoredBackendUser = (): ApiUser | null => {
  const raw = sessionStorage.getItem(BACKEND_USER_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as ApiUser;
  } catch {
    sessionStorage.removeItem(BACKEND_USER_STORAGE_KEY);
    return null;
  }
};

type AcquiredMicrosoftTokens = {
  accessToken: string | null;
  idToken: string | null;
};

const getErrorField = (error: unknown, field: 'errorCode' | 'name' | 'subError') => {
  if (!error || typeof error !== 'object' || !(field in error)) {
    return '';
  }

  const value = (error as Record<typeof field, unknown>)[field];
  return typeof value === 'string' ? value.toLowerCase() : '';
};

export const isAuthSessionExpiredError = (error: unknown) => {
  const errorCode = getErrorField(error, 'errorCode');
  const subError = getErrorField(error, 'subError');
  const errorName = getErrorField(error, 'name');

  return (
    AUTH_SESSION_EXPIRED_ERROR_CODES.has(errorCode) ||
    AUTH_SESSION_EXPIRED_ERROR_CODES.has(subError) ||
    errorName === 'interactionrequiredautherror'
  );
};

export const useAuthSession = (
  locationPath: string,
  options?: {
    embeddedMode?: boolean;
  },
) => {
  const embeddedMode = options?.embeddedMode === true;
  const msalIsAuthenticated = useIsAuthenticated();
  const { accounts, inProgress, instance } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    embeddedMode ? null : sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY),
  );
  const [backendUser, setBackendUser] = useState<ApiUser | null>(() =>
    embeddedMode ? null : getStoredBackendUser(),
  );
  const [isSessionReady, setIsSessionReady] = useState<boolean>(() => {
    if (embeddedMode) {
      return false;
    }

    const storedAccessToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    return Boolean(storedAccessToken && getStoredBackendUser());
  });
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionSyncAttempt, setSessionSyncAttempt] = useState(0);
  const lastMeSyncedPathRef = useRef<string | null>(null);
  const meSyncInFlightPathRef = useRef<string | null>(null);
  const isLogoutInProgressRef = useRef(false);
  const embeddedTokenWaitersRef = useRef<Array<(token: string | null) => void>>([]);

  const resolveEmbeddedTokenWaiters = useCallback((token: string | null) => {
    const waiters = embeddedTokenWaitersRef.current;
    embeddedTokenWaitersRef.current = [];
    waiters.forEach((resolve) => resolve(token));
  }, []);

  useEffect(() => {
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0] || null);
    }
  }, [accounts, instance]);

  const activeAccount = instance.getActiveAccount() || (accounts.length > 0 ? accounts[0] : null);
  const isAuthenticated = embeddedMode ? Boolean(accessToken) : msalIsAuthenticated;

  const clearSessionState = useCallback(() => {
    resolveEmbeddedTokenWaiters(null);
    setInMemoryBearerToken(null);
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(EXCHANGED_ACCOUNT_STORAGE_KEY);
    sessionStorage.removeItem(BACKEND_USER_STORAGE_KEY);
    setAccessToken(null);
    setBackendUser(null);
    setIsSessionReady(false);
    setSessionError(null);
    lastMeSyncedPathRef.current = null;
    meSyncInFlightPathRef.current = null;
  }, [resolveEmbeddedTokenWaiters]);

  const clearEmbeddedSession = useCallback(
    (message: string, requestFreshToken = false) => {
      clearSessionState();
      setSessionError(message);

      if (requestFreshToken) {
        requestTokenFromParent();
      }
    },
    [clearSessionState],
  );

  const logoutNow = useCallback(() => {
    if (isLogoutInProgressRef.current) {
      return;
    }

    isLogoutInProgressRef.current = true;
    clearSessionState();

    if (embeddedMode) {
      isLogoutInProgressRef.current = false;
      clearEmbeddedSession(
        'Embedded authentication is no longer available. Waiting for the host app to provide a valid token.',
        true,
      );
      return;
    }

    void instance
      .logoutRedirect({
        account: activeAccount || undefined,
        postLogoutRedirectUri: window.location.origin,
      })
      .catch((error) => {
        isLogoutInProgressRef.current = false;
        console.error('Failed to logout session', error);
        setSessionError('Unable to sign out automatically. Please try again.');
      });
  }, [activeAccount, clearEmbeddedSession, clearSessionState, embeddedMode, instance]);

  const acquireMicrosoftTokens = useCallback(
    async (forceRefresh = false): Promise<AcquiredMicrosoftTokens | null> => {
      const account = instance.getActiveAccount() || activeAccount || accounts[0] || null;
      const clientId = process.env.BUN_PUBLIC_AZURE_CLIENT_ID || '';

      if (!account || !clientId) {
        return null;
      }

      const response = await instance.acquireTokenSilent({
        scopes: [`api://${clientId}/access_as_user`],
        account,
        forceRefresh,
      });

      const nextAccessToken = response.accessToken || null;
      const nextIdToken = response.idToken || null;

      setAccessToken(nextAccessToken);

      if (nextAccessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, nextAccessToken);
      }

      if (nextIdToken) {
        sessionStorage.setItem(ID_TOKEN_STORAGE_KEY, nextIdToken);
      }

      return {
        accessToken: nextAccessToken,
        idToken: nextIdToken,
      };
    },
    [accounts, activeAccount, instance],
  );

  const refreshAccessToken = useCallback(async () => {
    if (embeddedMode) {
      return new Promise<string | null>((resolve) => {
        const timeoutId = window.setTimeout(() => {
          embeddedTokenWaitersRef.current = embeddedTokenWaitersRef.current.filter(
            (waiter) => waiter !== resolve,
          );
          resolve(null);
        }, EMBEDDED_REFRESH_WAIT_TIMEOUT_MS);

        embeddedTokenWaitersRef.current.push((token) => {
          window.clearTimeout(timeoutId);
          resolve(token);
        });

        requestTokenFromParent('401');
      });
    }

    const tokens = await acquireMicrosoftTokens(true);
    return tokens?.idToken || tokens?.accessToken || null;
  }, [acquireMicrosoftTokens, embeddedMode]);

  useEffect(() => {
    registerHttpAuthHandlers({
      refreshAccessToken,
      logout: logoutNow,
    });

    return () => {
      registerHttpAuthHandlers({});
    };
  }, [logoutNow, refreshAccessToken]);

  useEffect(() => {
    if (!embeddedMode) {
      return;
    }

    let isDisposed = false;

    const syncEmbeddedUser = async (token: string) => {
      setSessionError(null);
      setIsSessionReady(false);
      setAccessToken(token);
      setInMemoryBearerToken(token);

      try {
        const me = await getCurrentUser();
        if (isDisposed) {
          return;
        }

        setBackendUser(me);
        setIsSessionReady(true);
        resolveEmbeddedTokenWaiters(token);
      } catch (error) {
        if (isDisposed) {
          return;
        }

        setInMemoryBearerToken(null);
        setAccessToken(null);
        setBackendUser(null);
        setIsSessionReady(false);
        resolveEmbeddedTokenWaiters(null);
        if ((error as { status: number })?.status === 403) {
          setSessionError(
            (error as { message: string })?.message ??
              'Your are not authorized to access this application. Please contact support if you believe this is an error.',
          );
        } else {
          setSessionError(
            'Unable to verify the parent authentication token. Please refresh the host page.',
          );
        }
        console.error('Failed to verify embedded authentication token', error);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        return;
      }

      const token = extractParentToken(event.data);
      if (token) {
        void syncEmbeddedUser(token);
        return;
      }

      if (isParentTokenExpiredSignal(event.data)) {
        clearEmbeddedSession(
          'The parent token expired. Waiting for the host app to send a refreshed token.',
          true,
        );
        return;
      }

      if (isParentLogoutSignal(event.data)) {
        const reason = getParentLogoutReason(event.data);
        const message =
          reason === 'unauthenticated'
            ? 'The host app is not authenticated. Waiting for the host app to restore the session.'
            : reason === 'token-expired'
              ? 'The host session expired. Waiting for the host app to refresh authentication.'
              : 'You have been signed out from the parent app.';

        clearEmbeddedSession(message, reason === 'token-expired');
        return;
      }

      if (isParentAuthErrorSignal(event.data)) {
        clearEmbeddedSession(
          getParentAuthErrorMessage(event.data) ||
            'The host app could not provide authentication. Please refresh the host page.',
        );
      }
    };

    window.addEventListener('message', handleMessage);
    notifyParentChildReady(EMBEDDED_CHILD_APP_ID);

    return () => {
      isDisposed = true;
      resolveEmbeddedTokenWaiters(null);
      window.removeEventListener('message', handleMessage);
    };
  }, [clearEmbeddedSession, embeddedMode, resolveEmbeddedTokenWaiters]);

  useEffect(() => {
    if (embeddedMode) {
      return;
    }

    if (!isAuthenticated) {
      setIsSessionReady(false);
      setSessionError(null);
    }
  }, [embeddedMode, isAuthenticated]);

  const retrySessionSync = useCallback(() => {
    if (embeddedMode) {
      setSessionError(null);
      setIsSessionReady(false);
      requestTokenFromParent('refresh');
      return;
    }

    lastMeSyncedPathRef.current = null;
    meSyncInFlightPathRef.current = null;
    setSessionError(null);
    setIsSessionReady(false);
    setSessionSyncAttempt((attempt) => attempt + 1);
  }, [embeddedMode]);

  useEffect(() => {
    if (embeddedMode) {
      return;
    }

    if (!isAuthenticated || inProgress !== InteractionStatus.None) {
      return;
    }

    const account = activeAccount || accounts[0] || null;
    const clientId = process.env.BUN_PUBLIC_AZURE_CLIENT_ID || '';

    if (!account || !clientId) {
      return;
    }

    const syncKey = account.homeAccountId;
    const currentPath = locationPath;

    if (authSyncLocks.has(syncKey)) {
      return;
    }

    if (
      lastMeSyncedPathRef.current != null &&
      lastMeSyncedPathRef.current !== currentPath &&
      sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) &&
      getStoredBackendUser() &&
      accessToken &&
      backendUser
    ) {
      lastMeSyncedPathRef.current = currentPath;
      setIsSessionReady(true);
      return;
    }

    if (
      lastMeSyncedPathRef.current === currentPath ||
      meSyncInFlightPathRef.current === currentPath
    ) {
      if (backendUser && accessToken) {
        setIsSessionReady(true);
      }
      return;
    }

    authSyncLocks.add(syncKey);
    meSyncInFlightPathRef.current = currentPath;
    setSessionError(null);
    setIsSessionReady(false);

    const synchronizeAuthSession = async () => {
      try {
        let tokens = await acquireMicrosoftTokens(false);

        if (!tokens) {
          logoutNow();
          return;
        }

        const syncBackendUser = async (forceExchange: boolean) => {
          const storedExchangedAccountId = sessionStorage.getItem(EXCHANGED_ACCOUNT_STORAGE_KEY);

          if (forceExchange) {
            const refreshedTokens = await acquireMicrosoftTokens(true);
            if (refreshedTokens) {
              tokens = refreshedTokens;
            }
          }

          if (!tokens) {
            throw new ApiHttpError('Failed to refresh Microsoft token', { status: 401 });
          }

          if (tokens.idToken && (forceExchange || storedExchangedAccountId !== syncKey)) {
            await exchangeMicrosoftAuthToken({
              access_token: tokens.accessToken ?? undefined,
              id_token: tokens.idToken,
            });

            sessionStorage.setItem(EXCHANGED_ACCOUNT_STORAGE_KEY, syncKey);
          }

          return getCurrentUser();
        };

        let me: ApiUser;

        try {
          me = await syncBackendUser(false);
        } catch (meError) {
          if (meError instanceof ApiHttpError && meError.status === 401 && tokens.idToken) {
            sessionStorage.removeItem(EXCHANGED_ACCOUNT_STORAGE_KEY);
            me = await syncBackendUser(true);
          } else {
            throw meError;
          }
        }

        setBackendUser(me);
        sessionStorage.setItem(BACKEND_USER_STORAGE_KEY, JSON.stringify(me));
        lastMeSyncedPathRef.current = currentPath;
        setIsSessionReady(true);
      } catch (err) {
        if ((err instanceof ApiHttpError && err.status === 401) || isAuthSessionExpiredError(err)) {
          logoutNow();
          return;
        }

        if ((err instanceof ApiHttpError && err.status === 403) || isAuthSessionExpiredError(err)) {
          logoutNow();
          const errorMessage =
            (err as { message?: string })?.message ||
            'Your are not authorized to access this application. Please contact support if you believe this is an error.';
          toast.error(errorMessage);
          return;
        }

        console.error('Failed to synchronize login session', err);
        setSessionError('Unable to prepare your session. Please try again or sign in again.');
      } finally {
        authSyncLocks.delete(syncKey);
        meSyncInFlightPathRef.current = null;
      }
    };

    void synchronizeAuthSession();
  }, [
    acquireMicrosoftTokens,
    accessToken,
    accounts,
    activeAccount,
    backendUser,
    inProgress,
    instance,
    isAuthenticated,
    embeddedMode,
    locationPath,
    logoutNow,
    sessionSyncAttempt,
  ]);

  const isAuthBusy = useMemo(
    () =>
      inProgress === InteractionStatus.Startup ||
      inProgress === InteractionStatus.HandleRedirect ||
      inProgress === InteractionStatus.AcquireToken,
    [inProgress],
  );

  return {
    accounts: embeddedMode ? [] : accounts,
    activeAccount: embeddedMode ? null : activeAccount,
    instance,
    isAuthenticated,
    accessToken,
    backendUser,
    isSessionReady,
    sessionError,
    logoutNow,
    retrySessionSync,
    isAuthBusy,
    inProgress,
  };
};
